import { useState } from "react";
import axios from "axios";
import { useLanguage } from "../contexts/LanguageContext";
import { MapPin, Phone, Mail, Clock, MessageCircle, Send, CheckCircle } from "lucide-react";

import { API } from "../config";

export default function ContactPage() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      setError("Kļūda nosūtot ziņojumu. Lūdzu mēģiniet vēlreiz.");
    } finally {
      setLoading(false);
    }
  };

  const contactItems = [
    { icon: MapPin, label: t("contact.address"), value: t("contact.addressVal") },
    { icon: Phone, label: t("contact.phone"), value: "+371 25878923", href: "tel:+37125878923" },
    { icon: Mail, label: t("contact.email"), value: "info@realmendo.com", href: "mailto:info@realmendo.com" },
    { icon: Clock, label: t("contact.workHours"), value: t("contact.workHoursVal") },
  ];

  return (
    <div style={{ paddingTop: 72 }} className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-black py-16 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <span className="rmd-label text-[#FF6600] block mb-3">{t("contact.title")}</span>
          <h1
            className="text-white text-5xl md:text-7xl font-black uppercase tracking-tight leading-none"
            style={{ fontFamily: "Chivo, sans-serif" }}
            data-testid="contact-title"
          >
            {t("contact.title")}
          </h1>
          <p className="text-white/50 mt-4">{t("contact.subtitle")}</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-16">
        <div className="grid md:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div>
            <div className="space-y-6 mb-10">
              {contactItems.map(({ icon: Icon, label, value, href }) => (
                <div key={label} data-testid="contact-info-item" className="flex gap-4">
                  <div className="w-12 h-12 bg-[#FF6600] flex items-center justify-center flex-shrink-0 border-2 border-black" style={{ boxShadow: "2px 2px 0 #000" }}>
                    <Icon size={18} color="black" />
                  </div>
                  <div>
                    <p className="rmd-label mb-0.5">{label}</p>
                    {href ? (
                      <a href={href} className="font-semibold text-sm hover:text-[#FF6600] transition-colors no-underline">{value}</a>
                    ) : (
                      <p className="font-semibold text-sm">{value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* WhatsApp CTA */}
            <a
              href="https://wa.me/37125878923"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
              className="flex items-center gap-3 bg-[#25D366] text-white font-bold px-6 py-4 border-2 border-black hover:opacity-90 transition-opacity no-underline"
              style={{ boxShadow: "3px 3px 0 #000", display: "inline-flex" }}
            >
              <MessageCircle size={20} />
              <span style={{ fontFamily: "Chivo, sans-serif", fontWeight: 900, letterSpacing: "0.05em", fontSize: "0.875rem" }}>
                {t("contact.whatsapp")} – +371 25878923
              </span>
            </a>
          </div>

          {/* Contact Form */}
          <div>
            <h2
              className="text-2xl font-black uppercase tracking-tight mb-6 border-b-4 border-[#FF6600] pb-3"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              {t("contact.formTitle")}
            </h2>

            {success ? (
              <div data-testid="contact-success" className="flex flex-col items-center py-12 gap-4">
                <CheckCircle size={48} color="#FF6600" />
                <p className="font-bold text-lg">{t("contact.success")}</p>
                <button
                  onClick={() => setSuccess(false)}
                  className="rmd-btn-secondary text-sm"
                >
                  Jauns ziņojums
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="rmd-label block mb-1">{t("contact.name")} *</label>
                    <input
                      data-testid="contact-name-input"
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                      placeholder={t("contact.namePlaceholder")}
                      className="w-full border-2 border-black px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF6600]"
                    />
                  </div>
                  <div>
                    <label className="rmd-label block mb-1">{t("contact.phone")}</label>
                    <input
                      data-testid="contact-phone-input"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => set("phone", e.target.value)}
                      placeholder={t("contact.phonePlaceholder")}
                      className="w-full border-2 border-black px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF6600]"
                    />
                  </div>
                </div>

                <div>
                  <label className="rmd-label block mb-1">{t("contact.email")} *</label>
                  <input
                    data-testid="contact-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder={t("contact.emailPlaceholder")}
                    className="w-full border-2 border-black px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF6600]"
                  />
                </div>

                <div>
                  <label className="rmd-label block mb-1">{t("contact.message")} *</label>
                  <textarea
                    data-testid="contact-message-input"
                    required
                    value={form.message}
                    onChange={(e) => set("message", e.target.value)}
                    placeholder={t("contact.messagePlaceholder")}
                    rows={5}
                    className="w-full border-2 border-black px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF6600] resize-none"
                  />
                </div>

                {error && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2">{error}</div>
                )}

                <button
                  data-testid="contact-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="rmd-btn-primary w-full justify-center"
                >
                  {loading ? "..." : <><Send size={16} /> {t("contact.send")}</>}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black py-8 mt-8">
        <div className="max-w-6xl mx-auto px-6 md:px-12 text-center">
          <p className="text-white/30 text-xs">© {new Date().getFullYear()} RMD – Real Men Do · Cēsis, Latvija</p>
        </div>
      </footer>
    </div>
  );
}
